import { useState } from 'react';

import { autoUpdate, flip, offset, useFloating } from '@floating-ui/react-dom';
import { Menu as HeadlessMenu, Portal } from '@headlessui/react';
import styled from 'styled-components';

import { FixedFullscreenOverlay } from '@components/fixed-fullscreen-overlay';
import { FormFieldConditionJourney } from '@components/form/v2';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconCloseRegular } from '@components/icons/close-regular';
import { Menu } from '@components/menu';
import {
  MultiSegmentedFilters,
  SegmentConfig,
} from '@components/multi-segmented-filters/multi-segmented-filters';
import { isVariableSourceMissing } from '@components/text-editors/addons/field-mapping-variable-label';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import {
  applyComparatorChange,
  applyValueChange,
  getComparatorsForFieldType,
  isCompleteCondition,
} from './condition-helpers';
import {
  comparatorColor,
  ComparatorMenuItems,
  formatVariableValue,
  useVariableOptions,
  VariableIcon,
  VariableValueMenu,
} from './variable-condition-menus';

export type JourneyConditionRowProps = {
  condition: FormFieldConditionJourney;
  onSave(condition: FormFieldConditionJourney): void;
  onRemove(): void;
  onEditElement(condition: FormFieldConditionJourney): void;
  shouldGuide?: boolean;
  onGuideClose?(): void;
};

export const JourneyConditionRow = ({
  condition,
  onSave,
  onRemove,
  onEditElement,
  shouldGuide = false,
  onGuideClose,
}: JourneyConditionRowProps) => {
  const { t } = useScopedTranslate(locale);

  const elementLabel = condition.variable.label;
  const isVariableBroken = isVariableSourceMissing(condition.variable);
  const comparators = getComparatorsForFieldType(condition.variable.type);
  const comparatorLabel = t(`core.comparator.${condition.comparator}`).replace('...', '');
  const isComplete = isCompleteCondition(condition);
  const variableOptions = useVariableOptions(condition.variable);
  const valueLabel = formatVariableValue(
    condition.variable,
    isComplete ? condition.value : undefined,
    variableOptions,
    { placeholder: t('selectValue'), yes: t('core.yes'), no: t('core.no') }
  );

  const { x, y, refs, strategy } = useFloating({
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip()],
  });

  const segments: SegmentConfig[] = [
    {
      id: 'element',
      icon: <VariableIcon variable={condition.variable} />,
      text: elementLabel,
      textColor: isVariableBroken ? 'elementRed100' : undefined,
      tooltip: isVariableBroken ? t('brokenVariableTooltip') : elementLabel,
      isGrowable: true,
      shouldTruncate: true,
      onClick: () => onEditElement(condition),
    },
    {
      id: 'comparator',
      text: comparatorLabel,
      textColor: comparatorColor(condition.comparator),
      menuActions: ({ close }) => (
        <ComparatorMenuItems
          comparators={comparators}
          onSelect={(comparator) => {
            onSave(applyComparatorChange(condition, comparator));
            close();
          }}
        />
      ),
    },
    {
      id: 'selected-value',
      text: valueLabel,
      textColor: isComplete ? 'elementBlue100' : 'textHelper',
      tooltip: isComplete ? valueLabel : undefined,
      isGrowable: true,
      shouldTruncate: true,
      menuActions: ({ close }) => (
        <VariableValueMenu
          variable={condition.variable}
          comparator={condition.comparator}
          value={condition.value}
          onChange={(value) => onSave(applyValueChange(condition, value))}
          onClose={close}
        />
      ),
    },
    {
      id: 'close',
      icon: <IconCloseRegular />,
      onClick: onRemove,
    },
  ];

  return (
    <div ref={refs.setReference}>
      <MultiSegmentedFilters segments={segments} shouldWrap containerDataTestid="condition-row" />
      {shouldGuide && (
        <Portal>
          <DraftPanel
            ref={refs.setFloating}
            style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
          >
            <HeadlessMenu>
              <HeadlessMenu.Items static style={{ outline: 'none' }}>
                <DraftFlow
                  condition={condition}
                  comparators={comparators}
                  onSave={onSave}
                  onClose={onGuideClose}
                />
              </HeadlessMenu.Items>
            </HeadlessMenu>
          </DraftPanel>
          <FixedFullscreenOverlay onClick={onGuideClose} />
        </Portal>
      )}
    </div>
  );
};

type DraftFlowProps = {
  condition: FormFieldConditionJourney;
  comparators: ReturnType<typeof getComparatorsForFieldType>;
  onSave(condition: FormFieldConditionJourney): void;
  onClose?(): void;
};

// Walks the freshly-added condition through comparator then value so it never
// drops the user on a bare row they have to decode. A single comparator skips
// straight to the value step.
const DraftFlow = ({ condition, comparators, onSave, onClose }: DraftFlowProps) => {
  const { t } = useScopedTranslate(locale);
  const [step, setStep] = useState<'comparator' | 'value'>(
    comparators.length > 1 ? 'comparator' : 'value'
  );

  if (step === 'comparator') {
    return (
      <>
        <Menu.Label
          label={condition.variable.label}
          icon={<VariableIcon variable={condition.variable} />}
        />
        <Menu.Separator />
        <ComparatorMenuItems
          comparators={comparators}
          onSelect={(comparator) => {
            onSave(applyComparatorChange(condition, comparator));
            setStep('value');
          }}
        />
      </>
    );
  }

  return (
    <>
      <Menu.Item
        isHeader
        selectionType="open"
        label={t(`core.comparator.${condition.comparator}`).replace('...', '')}
        icon={<IconArrowLeft />}
        onSelect={() => setStep('comparator')}
      />
      <Menu.Separator />
      <VariableValueMenu
        variable={condition.variable}
        comparator={condition.comparator}
        value={condition.value}
        onChange={(value) => onSave(applyValueChange(condition, value))}
        onClose={onClose}
      />
    </>
  );
};

const DraftPanel = styled.div({
  backgroundColor: theme.color.backgroundLightDark,
  boxShadow: theme.shadow.high,
  borderRadius: pxToRem(8),
  padding: pxToRem(16),
  width: pxToRem(308),
  maxWidth: '100%',
  zIndex: 10000,
});

const locale = {
  en: {
    selectValue: 'Select value',
    brokenVariableTooltip: 'The action this condition depends on was deleted',
  },
};
